import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { forkJoin } from "rxjs";
import { PaymentAppService } from "../../../features/payments/services/payment-app.service";
import { AttachmentDownloader } from "../../../core/helpers/attachment-downloader";
import { SharedService } from "../../services/shared.service";
import { ApplicationStatus, STATUS_COLOR_MAP, getStatusLabel } from "../../../features/application/constants/application-status.enum";
import {
  PAYMENT_STATUS_FLOW,
  PAYMENT_STATUS_MAP,
  Payment,
  PaymentDetails,
  PaymentDocument,
  PaymentErrorSeverity,
  PaymentParty,
  PaymentStatus,
  PaymentStatusError,
  getPaymentApplicationLink,
  getPaymentStatusColor,
  humanizePaymentValue,
  mapPaymentDetails
} from "../../../features/payments/models/payment.model";

interface StatusHistoryStep {
  status: PaymentStatus;
  label: string;
  timestamp?: Date | string | null;
  state: 'passed' | 'current' | 'future';
  errors: PaymentStatusError[];
}

@Component({
    selector: 'app-payment-details-drawer',
    templateUrl: './payment-details-drawer.component.html',
    styleUrls: ['./payment-details-drawer.component.scss'],
    standalone: false
})
export class PaymentDetailsDrawerComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() payment: Payment | null = null;
  @Output() isOpenChange = new EventEmitter<boolean>();

  details: PaymentDetails | null = null;
  loadingDetails = false;
  drawerWidth: string | number = 500;

  /**
   * Computed once per `payment`/`details` change rather than as template getters — `*ngFor`
   * over a getter that returns a fresh array/objects every read defeats Angular's default
   * identity-based diffing and forces a full teardown+rebuild of the list on every change
   * detection cycle, which can pin the CPU and make the drawer appear to hang.
   */
  statusHistorySteps: StatusHistoryStep[] = [];
  currentStepNumber = 0;
  documents: PaymentDocument[] = [];

  readonly PaymentErrorSeverity = PaymentErrorSeverity;

  private loadedForId: string | null = null;

  constructor(private service: PaymentAppService, private shared: SharedService) {
    this.setDrawerWidth();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['payment'] || changes['isOpen']) {
      this.recomputeDerivedState();
    }

    if (changes['isOpen'] && this.isOpen && this.payment) {
      this.setDrawerWidth();

      if (this.loadedForId !== this.payment.applicationId) {
        this.loadDetails(this.payment.applicationId);
      }
    }
  }

  onClose(): void {
    this.isOpenChange.emit(false);
  }

  get statusColor(): string {
    return this.payment ? getPaymentStatusColor(this.payment.paymentStatus) : 'default';
  }

  get statusText(): string {
    if (!this.payment) {
      return '';
    }
    return PAYMENT_STATUS_MAP.get(this.payment.paymentStatus) || humanizePaymentValue(this.payment.paymentStatus);
  }

  get applicationLink(): any[] | null {
    if (!this.payment) {
      return null;
    }
    const applicationDbId = this.payment.applicationDbId ?? this.details?.applicationDbId;
    if (!applicationDbId) {
      return null;
    }
    return getPaymentApplicationLink({
      ...this.payment,
      applicationDbId,
      applicationTypeId: this.payment.applicationTypeId ?? this.details?.applicationTypeId
    });
  }

  get applicationLabel(): string {
    return this.details?.applicationNumber || this.payment?.applicationNumber || this.payment?.applicationId || '—';
  }

  /** "Application Type" per US-33a is the application channel (Online/Offline), not the payment type. Backend sends PORTAL, never ONLINE. */
  get applicationChannelText(): string {
    if (this.details?.applicationChannel === 'OFFLINE') {
      return 'Offline';
    }
    if (this.details?.applicationChannel === 'PORTAL') {
      return 'Online';
    }
    return '—';
  }

  get applicationStatusColor(): string {
    const status = this.details?.applicationStatus as ApplicationStatus | undefined;
    return status ? (STATUS_COLOR_MAP[status] || 'default') : 'default';
  }

  get applicationStatusText(): string {
    return this.details?.applicationStatus ? getStatusLabel(this.details.applicationStatus) : '';
  }

  get payer(): PaymentParty | null {
    return this.details?.payer || null;
  }

  get biller(): PaymentParty | null {
    return this.details?.biller || null;
  }

  documentDisplayName(doc: PaymentDocument): string {
    if (doc.code === 'ACT_OF_COMPLETION' && doc.documentVersion && doc.documentVersion > 1) {
      return `${doc.name} v${doc.documentVersion}`;
    }
    return doc.name;
  }

  downloadDocument(doc: PaymentDocument): void {
    if (!doc.available || doc.id == null) {
      return;
    }

    this.service.downloadDocument(doc.id).subscribe({
      next: blob => AttachmentDownloader.downloadFile(blob, { fileName: `${this.documentDisplayName(doc)}.pdf` })
    });
  }

  private loadDetails(applicationId: string): void {
    this.loadingDetails = true;
    this.details = null;

    forkJoin([this.shared.getCountryList(), this.service.getDetails(applicationId)]).subscribe({
      next: ([, raw]) => {
        this.details = mapPaymentDetails(raw, id => this.shared.getCountryNameById(id) || undefined);
        this.loadedForId = applicationId;
        this.loadingDetails = false;
        this.recomputeDerivedState();
      },
      error: () => {
        // Drawer still renders whatever is already available from the list row.
        this.loadedForId = applicationId;
        this.loadingDetails = false;
      }
    });
  }

  private setDrawerWidth(): void {
    this.drawerWidth = window.innerWidth < 768 ? '100%' : 500;
  }

  private recomputeDerivedState(): void {
    this.statusHistorySteps = this.buildStatusHistorySteps();

    const currentIdx = this.statusHistorySteps.findIndex(step => step.state === 'current');
    this.currentStepNumber = currentIdx >= 0
      ? currentIdx + 1
      : (this.statusHistorySteps.some(step => step.state === 'passed') ? this.statusHistorySteps.length : 0);

    this.documents = this.details?.documents || [
      { code: 'PAYMENT_INVOICE', name: 'Payment Invoice', available: false },
      { code: 'ACT_OF_COMPLETION', name: 'Act of Completion', available: false }
    ];
  }

  private buildStatusHistorySteps(): StatusHistoryStep[] {
    if (!this.payment) {
      return [];
    }

    const currentStatus = this.payment.paymentStatus as PaymentStatus;
    const currentIndex = PAYMENT_STATUS_FLOW.indexOf(currentStatus);

    const timestampByStatus = new Map<PaymentStatus, Date | string | null | undefined>(
      (this.details?.statusHistory || []).map(item => [item.status, item.timestamp])
    );
    const errorsByStatus = new Map<PaymentStatus, PaymentStatusError[]>();
    (this.details?.statusErrors || []).forEach(error => {
      errorsByStatus.set(error.status, [...(errorsByStatus.get(error.status) || []), error]);
    });

    return PAYMENT_STATUS_FLOW.map((status, index) => {
      const label = PAYMENT_STATUS_MAP.get(status) || humanizePaymentValue(status);

      let state: 'passed' | 'current' | 'future' = 'future';
      if (currentIndex >= 0) {
        if (index < currentIndex) {
          state = 'passed';
        } else if (index === currentIndex) {
          state = 'current';
        }
      }

      const timestamp = state === 'future'
        ? null
        : (timestampByStatus.get(status) ?? (state === 'current' ? this.payment?.paymentDate : null));

      const errors = errorsByStatus.get(status) || [];

      return { status, label, timestamp, state, errors };
    });
  }
}
